import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resolveShopNotificationPollMs } from "@/modules/notifications/domain/config";
import { shopNotificationsApi } from "./shop-notifications-client";

export const shopNotificationKeys = {
  all: (shopId: string) => ["shop-notifications", shopId] as const,
  summary: (shopId: string) =>
    ["shop-notifications", shopId, "summary"] as const,
  unreadCount: (shopId: string) =>
    ["shop-notifications", shopId, "unread-count"] as const,
  list: (
    shopId: string,
    params?: {
      limit?: number;
      offset?: number;
      unreadOnly?: boolean;
      type?: string;
    },
  ) => ["shop-notifications", shopId, "list", params ?? {}] as const,
};

export type ShopNotificationsListParams = {
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
  type?: string;
};

export function useShopNotificationSummaryQuery(
  shopId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: shopNotificationKeys.summary(shopId ?? ""),
    queryFn: () => shopNotificationsApi.getSummary(shopId!),
    enabled: enabled && Boolean(shopId),
    refetchInterval: () => {
      if (typeof document !== "undefined" && document.hidden) {
        return false;
      }
      return resolveShopNotificationPollMs();
    },
  });
}

export function useMarkShopNotificationReadMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shopNotificationsApi.markRead(shopId!, id),
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopNotificationKeys.all(shopId) });
      }
    },
  });
}

export function useMarkAllShopNotificationsReadMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => shopNotificationsApi.markAllRead(shopId!),
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopNotificationKeys.all(shopId) });
      }
    },
  });
}

export function useShopNotificationsListQuery(
  shopId: string | undefined,
  params: ShopNotificationsListParams,
  enabled = true,
) {
  return useQuery({
    queryKey: shopNotificationKeys.list(shopId ?? "", params),
    queryFn: () => shopNotificationsApi.list(shopId!, params),
    enabled: enabled && Boolean(shopId),
  });
}
